import React, { useState, useEffect } from "react";
import { db } from "../firebase-config";
import { collection, getDocs, updateDoc, doc, query, orderBy, where, serverTimestamp, addDoc } from "firebase/firestore";

const statusLabels = {
  new: { label: "Új érdeklődés", color: "bg-blue-500/20 text-blue-400" },
  qualified: { label: "Előminősített", color: "bg-green-500/20 text-green-400" },
  assigned: { label: "Partnerhez rendelve", color: "bg-purple-500/20 text-purple-400" },
  meeting_scheduled: { label: "Meeting időpontja egyeztetve", color: "bg-yellow-500/20 text-yellow-400" },
  offer_sent: { label: "Ajánlat elküldve", color: "bg-orange-500/20 text-orange-400" },
  contracted: { label: "Szerződött", color: "bg-emerald-500/20 text-emerald-400" },
  closed: { label: "Lezárt", color: "bg-gray-500/20 text-gray-400" },
  not_relevant: { label: "Nem releváns", color: "bg-red-500/20 text-red-400" }
};

const structureLabels = {
  fa: "Fa szerkezet",
  blokk: "Blokk",
  tegla: "Tégla"
};

export default function ConstructionLeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [meetingData, setMeetingData] = useState({
    date: "",
    time: "",
    type: "online",
    location: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch leads
      const leadsQuery = query(collection(db, "construction_leads"), orderBy("createdAt", "desc"));
      const leadsSnapshot = await getDocs(leadsQuery);
      const leadsData = leadsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLeads(leadsData);

      // Fetch partners
      const partnersQuery = query(collection(db, "construction_partners"), where("status", "==", "active"));
      const partnersSnapshot = await getDocs(partnersQuery);
      const partnersData = partnersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPartners(partnersData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await updateDoc(doc(db, "construction_leads", leadId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      fetchData();
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const assignPartner = async (leadId, partner) => {
    try {
      await updateDoc(doc(db, "construction_leads", leadId), {
        status: "assigned",
        assignedPartner: partner.companyName,
        assignedPartnerId: partner.id,
        assignedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update partner's assigned leads count
      await updateDoc(doc(db, "construction_partners", partner.id), {
        assignedLeads: (partner.assignedLeads || 0) + 1,
        updatedAt: serverTimestamp()
      });

      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      console.error("Error assigning partner:", error);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      const currentNotes = selectedLead.notes || [];
      await updateDoc(doc(db, "construction_leads", selectedLead.id), {
        notes: [...currentNotes, {
          text: newNote,
          createdAt: new Date().toISOString(),
          author: "Admin"
        }],
        updatedAt: serverTimestamp()
      });
      setNewNote("");
      fetchData();
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const scheduleMeeting = async () => {
    if (!meetingData.date || !meetingData.time || !selectedLead) return;
    try {
      const currentMeetings = selectedLead.meetings || [];
      await updateDoc(doc(db, "construction_leads", selectedLead.id), {
        status: "meeting_scheduled",
        meetings: [...currentMeetings, {
          ...meetingData,
          scheduledAt: new Date().toISOString(),
          status: "scheduled"
        }],
        updatedAt: serverTimestamp()
      });
      setShowMeetingModal(false);
      setMeetingData({ date: "", time: "", type: "online", location: "", notes: "" });
      fetchData();
    } catch (error) {
      console.error("Error scheduling meeting:", error);
    }
  };

  const getMatchingPartners = (lead) => {
    return partners.filter(p => 
      p.structures?.includes(lead.structure) &&
      p.regions?.includes(lead.region) &&
      p.capacity !== "high"
    );
  };

  const filteredLeads = filter === "all" 
    ? leads 
    : leads.filter(l => l.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-2 border-secondary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Építkezési Leadek</h2>
          <p className="text-gray-400 text-sm">Zárt partnermodell - minden lead a HOPPhomes-on keresztül</p>
        </div>
        
        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
              filter === "all" ? "bg-secondary text-white" : "bg-white/5 text-gray-400"
            }`}
          >
            Összes ({leads.length})
          </button>
          <button
            onClick={() => setFilter("new")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
              filter === "new" ? "bg-blue-500 text-white" : "bg-white/5 text-gray-400"
            }`}
          >
            Új ({leads.filter(l => l.status === "new").length})
          </button>
          <button
            onClick={() => setFilter("qualified")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
              filter === "qualified" ? "bg-green-500 text-white" : "bg-white/5 text-gray-400"
            }`}
          >
            Előminősített ({leads.filter(l => l.status === "qualified").length})
          </button>
          <button
            onClick={() => setFilter("assigned")}
            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
              filter === "assigned" ? "bg-purple-500 text-white" : "bg-white/5 text-gray-400"
            }`}
          >
            Hozzárendelve ({leads.filter(l => l.status === "assigned").length})
          </button>
        </div>
      </div>

      {/* Leads List */}
      <div className="grid gap-4">
        {filteredLeads.length === 0 ? (
          <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-white/5">
            <p className="text-gray-400">Nincs lead ebben a kategóriában</p>
          </div>
        ) : (
          filteredLeads.map(lead => (
            <div
              key={lead.id}
              className={`bg-[#1a1a1a] rounded-xl p-5 border border-white/5 cursor-pointer transition-all hover:border-white/[0.08] ${
                selectedLead?.id === lead.id ? "ring-2 ring-secondary" : ""
              }`}
              onClick={() => setSelectedLead(lead)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-white">{lead.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusLabels[lead.status]?.color}`}>
                      {statusLabels[lead.status]?.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="text-white">{lead.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Telefon:</span>
                      <p className="text-white">{lead.phone}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Régió:</span>
                      <p className="text-white">{lead.region}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Szerkezet:</span>
                      <p className="text-secondary font-medium">{structureLabels[lead.structure]}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-400">
                      📏 {lead.area ? `${lead.area} m²` : "Nincs megadva"}
                    </span>
                    <span className="text-gray-400">
                      🏗️ Telek: {lead.hasLand === "yes" ? "Van" : "Nincs"}
                    </span>
                    {lead.assignedPartner && (
                      <span className="text-purple-400">
                        👷 Partner: {lead.assignedPartner}
                      </span>
                    )}
                  </div>

                  {lead.message && (
                    <p className="text-gray-400 text-sm mt-2 italic">"{lead.message}"</p>
                  )}
                </div>

                <div className="text-right text-xs text-gray-500">
                  {lead.createdAt?.toDate?.().toLocaleDateString("hu-HU") || "N/A"}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Selected Lead Details Panel */}
      {selectedLead && (
        <div className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-[#111] border-l border-white/10 shadow-2xl z-40 overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{selectedLead.name}</h3>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all"
              >
                ✕
              </button>
            </div>

            {/* Status */}
            <div className="mb-6">
              <label className="block text-gray-400 text-sm mb-2">Státusz</label>
              <select
                value={selectedLead.status}
                onChange={(e) => updateLeadStatus(selectedLead.id, e.target.value)}
                className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
              >
                {Object.entries(statusLabels).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Contact Info */}
            <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/5">
              <h4 className="text-white font-medium mb-3">Kapcsolat</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Email:</span> <span className="text-white">{selectedLead.email}</span></p>
                <p><span className="text-gray-500">Telefon:</span> <span className="text-white">{selectedLead.phone}</span></p>
                <p><span className="text-gray-500">Régió:</span> <span className="text-white">{selectedLead.region}</span></p>
              </div>
            </div>

            {/* Project Info */}
            <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/5">
              <h4 className="text-white font-medium mb-3">Projekt</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Szerkezet:</span> <span className="text-secondary font-medium">{structureLabels[selectedLead.structure]}</span></p>
                <p><span className="text-gray-500">Alapterület:</span> <span className="text-white">{selectedLead.area ? `${selectedLead.area} m²` : "Nincs megadva"}</span></p>
                <p><span className="text-gray-500">Telek:</span> <span className="text-white">{selectedLead.hasLand === "yes" ? "Van saját telke" : "Még nincs telke"}</span></p>
              </div>
            </div>

            {/* Partner Assignment */}
            <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/5">
              <h4 className="text-white font-medium mb-3">Partner hozzárendelés</h4>
              {selectedLead.assignedPartner ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{selectedLead.assignedPartner}</p>
                    <p className="text-gray-500 text-xs">Hozzárendelve</p>
                  </div>
                  <span className="text-green-400">✓</span>
                </div>
              ) : (
                <div>
                  <p className="text-gray-400 text-sm mb-3">
                    Még nincs partner hozzárendelve. {getMatchingPartners(selectedLead).length} megfelelő partner elérhető.
                  </p>
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 font-medium py-2 rounded-lg transition-all"
                  >
                    Partner kiválasztása
                  </button>
                </div>
              )}
            </div>

            {/* Meeting */}
            <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/5">
              <h4 className="text-white font-medium mb-3">Meetingek</h4>
              {selectedLead.meetings?.length > 0 ? (
                <div className="space-y-2">
                  {selectedLead.meetings.map((meeting, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-3">
                      <p className="text-white text-sm">{meeting.date} - {meeting.time}</p>
                      <p className="text-gray-400 text-xs">{meeting.type === "online" ? "Online" : meeting.location}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm mb-3">Nincs még meeting időpont</p>
              )}
              <button
                onClick={() => setShowMeetingModal(true)}
                className="w-full bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 font-medium py-2 rounded-lg transition-all mt-2"
              >
                + Meeting szervezése
              </button>
            </div>

            {/* Notes */}
            <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4 border border-white/5">
              <h4 className="text-white font-medium mb-3">Jegyzetek</h4>
              <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                {selectedLead.notes?.map((note, idx) => (
                  <div key={idx} className="bg-white/5 rounded-lg p-2 text-sm">
                    <p className="text-white">{note.text}</p>
                    <p className="text-gray-500 text-xs mt-1">{note.author} - {new Date(note.createdAt).toLocaleDateString("hu-HU")}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Új jegyzet..."
                  className="flex-1 bg-[#2a2a2a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                />
                <button
                  onClick={addNote}
                  className="bg-secondary hover:bg-hover text-white px-4 py-2 rounded-lg transition-all"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Assignment Modal */}
      {showAssignModal && selectedLead && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-6 max-w-lg w-full border border-white/10">
            <h3 className="text-xl font-bold text-white mb-2">Partner kiválasztása</h3>
            <p className="text-gray-400 text-sm mb-6">
              A HOPPhomes kiválasztja a megfelelő kivitelezőt. Az ügyfél ezt nem látja.
            </p>

            <div className="space-y-3 max-h-80 overflow-y-auto mb-6">
              {getMatchingPartners(selectedLead).length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  Nincs megfelelő partner a kiválasztott régióhoz és szerkezethez.
                </p>
              ) : (
                getMatchingPartners(selectedLead).map(partner => (
                  <button
                    key={partner.id}
                    onClick={() => assignPartner(selectedLead.id, partner)}
                    className="w-full bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-xl p-4 text-left border border-white/5 hover:border-secondary/50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{partner.companyName}</p>
                        <p className="text-gray-400 text-sm">{partner.contactName}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          partner.capacity === "low" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
                        }`}>
                          {partner.capacity === "low" ? "Szabad kapacitás" : "Közepes terheltség"}
                        </span>
                        <p className="text-gray-500 text-xs mt-1">{partner.assignedLeads || 0} aktív lead</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => setShowAssignModal(false)}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 rounded-lg transition-all"
            >
              Mégse
            </button>
          </div>
        </div>
      )}

      {/* Meeting Modal */}
      {showMeetingModal && selectedLead && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-6 max-w-lg w-full border border-white/10">
            <h3 className="text-xl font-bold text-white mb-2">Meeting szervezése</h3>
            <p className="text-gray-400 text-sm mb-6">
              A HOPPhomes szervezi a meetinget és végigkíséri a folyamatot.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Dátum *</label>
                  <input
                    type="date"
                    value={meetingData.date}
                    onChange={(e) => setMeetingData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Időpont *</label>
                  <input
                    type="time"
                    value={meetingData.time}
                    onChange={(e) => setMeetingData(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1">Típus</label>
                <select
                  value={meetingData.type}
                  onChange={(e) => setMeetingData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="online">Online (Zoom/Meet)</option>
                  <option value="personal">Személyes</option>
                  <option value="phone">Telefonos</option>
                </select>
              </div>

              {meetingData.type === "personal" && (
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Helyszín</label>
                  <input
                    type="text"
                    value={meetingData.location}
                    onChange={(e) => setMeetingData(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                    placeholder="Cím"
                  />
                </div>
              )}

              <div>
                <label className="block text-gray-300 text-sm mb-1">Megjegyzés</label>
                <textarea
                  value={meetingData.notes}
                  onChange={(e) => setMeetingData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white resize-none"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowMeetingModal(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-medium py-3 rounded-lg transition-all"
              >
                Mégse
              </button>
              <button
                onClick={scheduleMeeting}
                className="flex-1 bg-secondary hover:bg-hover text-white font-medium py-3 rounded-lg transition-all"
              >
                Meeting létrehozása
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
